class PopoverDropdown extends HTMLElement {
  // Class-scoped, not module-scoped: every asset here is a classic script sharing
  // one global lexical scope, so a top-level const collides across components.
  static CHEVRON_OPEN_CLASS = 'rotate-180';

  constructor() {
    super();
    this.activeIndex = -1;
    this.handleOutsideClick = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-popover-trigger]');
    this.listbox = this.querySelector('[data-popover-listbox]');
    this.triggerLabel = this.querySelector('[data-popover-trigger-label]');
    this.chevron = this.querySelector('.popover-dropdown__chevron');
    this.options = [...this.querySelectorAll('[data-popover-option]')];

    if (!this.trigger || this.trigger.disabled) return;

    this.trigger.addEventListener('click', () => this.toggleOpen());
    this.trigger.addEventListener('keydown', (event) => this.handleTriggerKeydown(event));
    this.listbox.addEventListener('keydown', (event) => this.handleListboxKeydown(event));
    this.listbox.addEventListener('click', (event) => {
      const option = event.target.closest('[data-popover-option]');
      if (option) this.select(option);
    });

    this.handleOutsideClick = (event) => {
      if (!this.contains(event.target)) this.close({ returnFocus: false });
    };
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    if (this.handleOutsideClick) {
      document.removeEventListener('click', this.handleOutsideClick);
    }
  }

  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.open();
      this.setActive(0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
      this.setActive(this.options.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
    }
  }

  handleListboxKeydown(event) {
    const { length } = this.options;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setActive((this.activeIndex + 1) % length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setActive((this.activeIndex - 1 + length) % length);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(this.options[this.activeIndex]);
    } else if (event.key === 'Escape') {
      this.close({ returnFocus: true });
    } else if (event.key === 'Tab') {
      this.close({ returnFocus: false });
    }
  }

  open() {
    this.listbox.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.chevron?.classList.add(PopoverDropdown.CHEVRON_OPEN_CLASS);

    const selectedIndex = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.setActive(selectedIndex >= 0 ? selectedIndex : 0);

    this.dispatchEvent(new CustomEvent('popover:open', { bubbles: true }));
  }

  close({ returnFocus = true } = {}) {
    this.listbox.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.chevron?.classList.remove(PopoverDropdown.CHEVRON_OPEN_CLASS);

    if (this.activeIndex >= 0) {
      this.options[this.activeIndex]?.setAttribute('tabindex', '-1');
      this.activeIndex = -1;
    }

    if (returnFocus) this.trigger.focus();

    this.dispatchEvent(new CustomEvent('popover:close', { bubbles: true }));
  }

  toggleOpen() {
    if (this.listbox.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  // Roving tabindex only. The active option's highlight is the `focus:` variant in
  // the template — a JS-toggled Tailwind class is never emitted by the JIT.
  setActive(index) {
    const nextOption = this.options[index];
    if (!nextOption) return;

    if (this.activeIndex >= 0) this.options[this.activeIndex]?.setAttribute('tabindex', '-1');

    nextOption.setAttribute('tabindex', '0');
    nextOption.focus();

    this.activeIndex = index;
  }

  select(option) {
    if (!option) return;

    const { value } = option.dataset;
    const label = option.textContent.trim();

    this.triggerLabel.textContent = label;

    for (const item of this.options) {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    }

    this.close({ returnFocus: true });

    this.dispatchEvent(new CustomEvent('popover:select', { bubbles: true, detail: { value, label } }));
  }
}

if (!customElements.get('popover-dropdown')) {
  customElements.define('popover-dropdown', PopoverDropdown);
}
